<template>
  <div>
    <b-card>
      <b-row class="mb-2 align-items-center">
        <b-col>
          <span class="text-muted">All active flocks health overview</span>
        </b-col>
        <b-col class="text-right">
          <b-button variant="outline-primary" :disabled="loading" @click="fetchData">
            <b-spinner v-if="loading" small class="mr-1" />
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Loading skeleton grid -->
      <b-row v-if="loading">
        <b-col v-for="n in 6" :key="n" md="4" lg="3" class="mb-3">
          <b-card>
            <b-skeleton width="60%" class="mb-2"/>
            <b-skeleton width="40%" class="mb-3"/>
            <b-skeleton type="avatar" class="mx-auto mb-2" style="width:70px;height:70px;border-radius:50%"/>
            <b-skeleton width="50%" class="mx-auto mb-1"/>
            <b-skeleton width="80%" class="mb-1"/>
            <b-skeleton width="80%" class="mb-1"/>
          </b-card>
        </b-col>
      </b-row>

      <div v-else-if="!flocks.length" class="d-flex flex-column align-items-center py-5">
        <b-img
          :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')"
          width="140"
        />
        <h5 class="mt-2 text-secondary">No active flocks found</h5>
        <p class="text-muted mb-0">Health scores will appear here once a flock is active.</p>
      </div>

      <b-row v-else>
        <b-col
          v-for="flock in flocks"
          :key="flock.flock_id"
          md="4"
          lg="3"
          class="mb-3"
        >
          <b-card
            class="text-center h-100 health-card"
            :class="`border-${scoreVariant(flock.score)}`"
            style="border-top: 4px solid;"
          >
            <h6 class="text-muted mb-0">Flock #{{ flock.flock_id }}</h6>
            <small class="text-muted">{{ flock.farm_name }}</small>
            <div class="my-3">
              <span
                class="score-circle"
                :class="`bg-${scoreVariant(flock.score)}`"
              >
                {{ flock.score }}
              </span>
            </div>
            <b-badge :variant="scoreVariant(flock.score)" class="mb-2">{{ scoreLabel(flock.score) }}</b-badge>
            <hr />
            <div class="text-left small">
              <div class="d-flex justify-content-between">
                <span>Mortality</span>
                <strong>{{ flock.components && flock.components.mortality || '—' }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>Feed</span>
                <strong>{{ flock.components && flock.components.feed || '—' }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>Water</span>
                <strong>{{ flock.components && flock.components.water || '—' }}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>Weight</span>
                <strong>{{ flock.components && flock.components.weight || '—' }}</strong>
              </div>
            </div>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mt-2"
              :to="`/health-score/${flock.farm_id}/${flock.shed_id}/${flock.flock_id}`"
            >
              Details
            </b-button>
          </b-card>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { BCard, BRow, BCol, BButton, BBadge, BSpinner } from 'bootstrap-vue'

export default {
  name: 'HealthScoreDashboard',
  components: { BCard, BRow, BCol, BButton, BBadge, BSpinner },
  data() {
    return {
      loading: false,
      flocks: [],
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const { data } = await axios.get('web/health-scores/dashboard')
        this.flocks = data.data || data.flocks || data || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load health scores', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    scoreVariant(score) {
      if (score >= 80) return 'success'
      if (score >= 60) return 'warning'
      if (score >= 40) return 'orange'
      return 'danger'
    },
    scoreLabel(score) {
      if (score >= 80) return 'Healthy'
      if (score >= 60) return 'Fair'
      if (score >= 40) return 'At Risk'
      return 'Critical'
    },
  },
}
</script>

<style scoped>
.score-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  font-size: 1.6rem;
  font-weight: 700;
  color: white;
}
.health-card {
  transition: box-shadow 0.2s;
}
.health-card:hover {
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
}
.bg-orange { background-color: #ff9f43 !important; }
</style>
